import { urls } from '@app/services/api/urls';

// The Redpen bug and feature-request widget is switched on in Django admin
// (Site widget settings), not at build time, so a deploy ships with it off and
// an admin turns it on later without a rebuild (ZLAYT-1542).
//
// VITE_REDPEN_WIDGET_ID is a local dev only override: on the Vite dev server
// (import.meta.env.DEV) it wins and the backend is not asked. Built bundles
// ignore it.
//
// Plain fetch, not the shared axios instance: axios would record a failed
// call as the "last failed request" shown in the next error toast, and would
// send a 401 to the sign-in page. A widget that fails to load must stay
// silent (ZLAYT-1421).

const REDPEN_SCRIPT_SRC = 'https://app.redpen.ai/redpenWebWidget.js';
const FETCH_TIMEOUT_MS = 10_000;

type PublicWidgets = {
  redpen?: { enabled?: unknown; widget_id?: unknown; menu_link?: unknown };
};

export function injectRedpenWidget(widgetId: string): void {
  if (!widgetId || document.getElementById('redpen-widget-script')) {
    return;
  }
  const cfg = document.createElement('script');
  cfg.id = 'redpen-widget-data';
  cfg.type = 'text/javascript';
  // JSON.stringify so an id from the backend can never break out of the string.
  cfg.text = `window.redpenWidgetConfig = { widgetId: ${JSON.stringify(widgetId)} };`;
  document.body.appendChild(cfg);

  const s = document.createElement('script');
  s.id = 'redpen-widget-script';
  s.defer = true;
  s.async = true;
  s.src = REDPEN_SCRIPT_SRC;
  // The "Send feedback" entry shows only once the script has loaded, and
  // goes away if it fails (blocked by an extension or a network filter).
  s.addEventListener('load', () => setScriptState('ready'));
  s.addEventListener('error', () => setScriptState('failed'));
  document.body.appendChild(s);
}

export type RedpenWidgetConfig = {
  widgetId: string | null;
  /** Show the "Send feedback" side menu entry (ZLAYT-1667). */
  menuLink: boolean;
};

const OFF: RedpenWidgetConfig = { widgetId: null, menuLink: false };

export async function fetchRedpenWidgetConfig(
  apiBase: string | undefined = import.meta.env.VITE_APP_API_URL
): Promise<RedpenWidgetConfig> {
  if (!apiBase) {
    return OFF;
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(`${apiBase}${urls.publicWidgets}`, {
      method: 'GET',
      credentials: 'omit',
      signal: controller.signal,
    });
    if (!response.ok) {
      return OFF;
    }
    const body = (await response.json()) as PublicWidgets;
    const redpen = body?.redpen;
    if (
      redpen?.enabled === true &&
      typeof redpen.widget_id === 'string' &&
      redpen.widget_id.trim()
    ) {
      return {
        widgetId: redpen.widget_id.trim(),
        menuLink: redpen.menu_link === true,
      };
    }
    return OFF;
  } catch {
    return OFF;
  } finally {
    clearTimeout(timer);
  }
}

export async function fetchRedpenWidgetId(
  apiBase: string | undefined = import.meta.env.VITE_APP_API_URL
): Promise<string | null> {
  return (await fetchRedpenWidgetConfig(apiBase)).widgetId;
}

// The side menu asks whether to show "Send feedback". The answer arrives
// after the app has rendered, so the menu subscribes to it.
let menuLink = false;
const menuLinkListeners = new Set<() => void>();

function setRedpenMenuLink(value: boolean): void {
  const before = getRedpenMenuLink();
  menuLink = value;
  if (getRedpenMenuLink() !== before) {
    menuLinkListeners.forEach((listener) => listener());
  }
}

type ScriptState = 'loading' | 'ready' | 'failed';
let scriptState: ScriptState = 'loading';

function setScriptState(value: ScriptState): void {
  const before = getRedpenMenuLink();
  scriptState = value;
  if (getRedpenMenuLink() !== before) {
    menuLinkListeners.forEach((listener) => listener());
  }
}

/** The backend switched the entry on and Redpen's script has loaded. */
export function getRedpenMenuLink(): boolean {
  return menuLink && scriptState === 'ready';
}

export function subscribeRedpenMenuLink(listener: () => void): () => void {
  menuLinkListeners.add(listener);
  return () => {
    menuLinkListeners.delete(listener);
  };
}

export function resetRedpenMenuLinkForTests(): void {
  menuLink = false;
  scriptState = 'loading';
  inFlight = undefined;
  menuLinkListeners.clear();
}

export async function loadRedpenWidget(
  override: string | undefined = import.meta.env.VITE_REDPEN_WIDGET_ID,
  isDevServer: boolean = import.meta.env.DEV
): Promise<void> {
  // The override counts only on the local dev server. Every built bundle
  // (dev, staging, production) asks the backend, because CI copies a
  // variables file into .env and a stray id there must not switch it on.
  const localId = isDevServer ? override?.trim() : '';
  if (localId) {
    // The local override loads the widget only; the "Send feedback" entry
    // follows the backend's menu_link and is not shown on this path.
    injectRedpenWidget(localId);
    return;
  }
  const config = await fetchRedpenWidgetConfig();
  if (config.widgetId) {
    injectRedpenWidget(config.widgetId);
    setRedpenMenuLink(config.menuLink);
  }
}

// ---- "Send feedback" from the side menu (ZLAYT-1667) ----
//
// Redpen draws its own Feedback tab: a slim tab on the right edge that only
// appears once Redpen's sign-in (which needs Google reCAPTCHA) has finished.
// Jack could not find it. The menu entry asks Redpen to open its form
// (window.RedpenWidget.openWidget). Redpen only honours that when the widget's
// "open from a custom button" option is on in the Redpen dashboard; otherwise
// we light up the tab so it can be found, and when Redpen has not loaded at
// all (still starting, or blocked by the browser) we say so.

export type RedpenOpenResult = 'opened' | 'tab' | 'unavailable';

const WRAPPER_ID = 'redpen-widget-wrapper';
const HIGHLIGHT_CLASS = 'redpen-tab-highlight';
const OPEN_CHECK_MS = 1200;
const HIGHLIGHT_MS = 6000;
// Closed, the tab is 34 px wide; open, Redpen covers the whole window.
const OPEN_MIN_WIDTH = 200;

type RedpenGlobal = { openWidget?: (config?: unknown) => void };

let highlightTimer: ReturnType<typeof setTimeout> | undefined;
// One attempt at a time: quick repeat clicks share it instead of calling
// openWidget again (which could toggle Redpen shut) and stacking messages.
let inFlight: Promise<RedpenOpenResult> | undefined;

function wrapperWidth(): number {
  const el = document.getElementById(WRAPPER_ID);
  return el ? el.getBoundingClientRect().width : 0;
}

export function openRedpenFeedback(): Promise<RedpenOpenResult> {
  if (!inFlight) {
    inFlight = attemptOpen().finally(() => {
      inFlight = undefined;
    });
  }
  return inFlight;
}

async function attemptOpen(): Promise<RedpenOpenResult> {
  const redpen = (window as unknown as { RedpenWidget?: RedpenGlobal })
    .RedpenWidget;
  if (typeof redpen?.openWidget === 'function') {
    try {
      redpen.openWidget();
    } catch {
      // Redpen's own failure must not break the menu.
    }
  }
  await new Promise((resolve) => setTimeout(resolve, OPEN_CHECK_MS));

  const width = wrapperWidth();
  if (width >= OPEN_MIN_WIDTH) {
    return 'opened';
  }
  const el = document.getElementById(WRAPPER_ID);
  if (el && width > 0) {
    el.classList.add(HIGHLIGHT_CLASS);
    // A second click restarts the time rather than ending it early.
    clearTimeout(highlightTimer);
    // Looked up again: Redpen may have redrawn its tab in the meantime.
    highlightTimer = setTimeout(
      () =>
        document.getElementById(WRAPPER_ID)?.classList.remove(HIGHLIGHT_CLASS),
      HIGHLIGHT_MS
    );
    return 'tab';
  }
  return 'unavailable';
}
